import {
  DownloadErrorDetails,
  ImageDownloadFailure,
  ImageDownloadItem,
  ImagePath,
} from './interfaces';
import { ImageDownloadProgress } from './types';

/**
 * Returns true if the provided value is a non-null object, false otherwise.
 * Intended to be used for checking the error object from failed image downloads.
 *
 * @param value The value to check.
 * @returns True if the value is a non-null object, false otherwise.
 */
export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

/**
 * Gets detailed information about a download error,
 * including the message and optional HTTP status code.
 *
 * @param error The error object to extract details from.
 * @returns An object containing the error message and optional HTTP status code.
 */
export const getErrorDetails = (error: unknown): DownloadErrorDetails => {
  if (error instanceof Error) {
    return { message: error.message };
  }

  if (!isRecord(error)) {
    return { message: String(error) };
  }

  const nestedError = isRecord(error.error) ? error.error : undefined;
  const responseValue = nestedError?.response ?? error.response;
  const response = isRecord(responseValue) ? responseValue : undefined;
  const data = isRecord(response?.data) ? response?.data : undefined;
  const messageCandidates = [
    data?.message,
    data?.error,
    nestedError?.message,
    error.message,
  ];
  const message = messageCandidates.find(
    (candidate): candidate is string =>
      typeof candidate === 'string' && candidate.length > 0,
  );

  return {
    message: message ?? 'DOWNLOAD_IMAGES_UNKNOWN_ERROR',
    httpStatus:
      typeof response?.status === 'number' ? response.status : undefined,
  };
};

/**
 * Builds the relative path used for an image inside the downloaded ZIP archive.
 *
 * Paths are in the shape of
 * `<companyName>_images_<timestamp>/<drillholeName>/<imageType>/`
 * The image file names are in the shape of:
 * `<drillholeName>_<fromDepth>_<toDepth>_<imageType>.<extension>`
 *
 * Example: goldspot_images_09.04.2026_14.57/CMD25-032/dry/CMD25-032_247.37_251.76_dry.jpg
 *
 * @param companyName The company name used as the archive root directory.
 * @param image The image metadata used to build the archive path.
 * @param timeStamp The timestamp used to create a unique archive path.
 * @returns A relative ZIP entry path grouped by drillhole and image type.
 */
export const getImageFileName = (
  companyName: string,
  image: ImagePath,
  timeStamp: string,
): string => {
  const path = `${companyName}_images_${timeStamp}/${image.drillholeName}/${image.imageType || 'unknown type'}`;
  const extension = image.path?.split('/').pop()?.split('.').pop() ?? '.jpg';
  const file = `${image.drillholeName}_${image.fromDepth}_${image.toDepth}_${image.imageType || 'unknown type'}.${extension}`;
  return `${path}/${file}`;
};

/**
 * Creates an initial zeroed progress record for each drillhole in a download.
 *
 * @param imageDownloadList The drillholes and images included in the download.
 * @returns Progress entries with processed, succeeded, and failed images counts set to 0.
 */
export const getInitialDownloadProgress = (
  imageDownloadList: ImageDownloadItem[],
): ImageDownloadProgress =>
  imageDownloadList.reduce<ImageDownloadProgress>(
    (progress, item) => ({
      ...progress,
      [item.id]: { processed: 0, succeeded: 0, failed: 0 },
    }),
    {},
  );

/**
 * Determines whether every selected image completed successfully.
 *
 * @param imageDownloadList The images included in the download.
 * @param downloadProgress Per-drillhole download progress.
 * @param downloadFailures Individual image download failures.
 * @param fatalDownloadError A fatal archive-generation error, if any.
 * @param isDownloading Whether the download is currently active.
 * @param wasDownloadCancelled Whether the download was cancelled.
 * @returns True when all selected images were downloaded successfully.
 */
export const isSuccessfulWithoutErrors = ({
  imageDownloadList,
  downloadProgress,
  downloadFailures,
  fatalDownloadError,
  isDownloading,
  wasDownloadCancelled,
}: {
  imageDownloadList: ImageDownloadItem[];
  downloadProgress: ImageDownloadProgress;
  downloadFailures: ImageDownloadFailure[];
  fatalDownloadError: string | null;
  isDownloading: boolean;
  wasDownloadCancelled: boolean;
}): boolean => {
  if (
    isDownloading ||
    wasDownloadCancelled ||
    fatalDownloadError ||
    downloadFailures.length > 0
  ) {
    return false;
  }

  const totalImageCount = imageDownloadList.reduce(
    (total, { images }) => total + images.length,
    0,
  );
  const succeededImageCount = Object.values(downloadProgress).reduce(
    (total, { succeeded }) => total + succeeded,
    0,
  );

  return totalImageCount > 0 && succeededImageCount === totalImageCount;
};

/**
 * Calculates a drillhole's processed-image percentage and clamps it to 0–100.
 *
 * @param processedImageCount The number of images already processed.
 * @param totalImageCount The total number of images for the drillhole.
 * @returns The clamped progress percentage, or 0 when there are no images.
 */
export const getDownloadProgressPercentage = (
  processedImageCount: number,
  totalImageCount: number,
): number => {
  if (totalImageCount === 0) {
    return 0;
  }

  return Math.min(
    100,
    Math.max(0, (processedImageCount / totalImageCount) * 100),
  );
};

/**
 * Creates the user-facing failure record for an image download attempt.
 *
 * @param image The image that failed to download.
 * @param drillholeId The ID of the drillhole containing the image.
 * @param message The final error message describing the failure.
 * @param httpStatus Optional HTTP status associated with the failure.
 * @returns A failure record containing image identity, location, and error details.
 */
export const createImageDownloadFailure = (
  image: ImagePath,
  drillholeId: string,
  message: string,
  httpStatus?: number,
): ImageDownloadFailure => ({
  imageId: image.imageId,
  imageName: image.name,
  path: image.path,
  imageType: image.imageType,
  drillholeId,
  drillholeName: image.drillholeName,
  message,
  httpStatus,
});
